import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  FolderKanban,
  Loader2,
  Monitor,
  Plus,
  Search,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { RowActions } from "@/components/ui/RowActions";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import {
  useAssetCategories,
  useAssetConditions,
  useAssetLocations,
  useAssetList,
  useAssetStats,
  useDeleteAsset,
} from "../hooks/useAssets";
import { NewAssetDrawer } from "../components/NewAssetDrawer";
import { AssignAssetModal } from "../components/AssignAssetModal";
import {
  ConditionBadge,
  StatusBadge,
  categoryColor,
  categoryIcon,
  warrantyCell,
} from "../components/assetBadges";
import type { AssetListItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

const ASSET_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "name", label: "Name" },
  { key: "category", label: "Category" },
  { key: "brand", label: "Brand / Model", defaultVisible: false },
  { key: "condition", label: "Condition" },
  { key: "status", label: "Status" },
  { key: "assignedToName", label: "Assigned To" },
  { key: "projectName", label: "Project" },
  { key: "warranty", label: "Warranty", defaultVisible: false },
];

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function StatCard({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "green" | "blue" | "amber" | "red";
}) {
  const color =
    tone === "green"
      ? "hsl(142,72%,28%)"
      : tone === "blue"
        ? "hsl(210,100%,28%)"
        : tone === "amber"
          ? "hsl(35,90%,32%)"
          : tone === "red"
            ? "hsl(0,70%,40%)"
            : "hsl(215,25%,27%)";
  return (
    <div className="rounded-xl border bg-white p-4" style={{ borderColor: BORDER }}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export default function AssetListPage() {
  const navigate = useNavigate();
  const { data: stats } = useAssetStats();
  const { data: categories = [] } = useAssetCategories();
  const { data: conditions = [] } = useAssetConditions();
  const { data: locations = [] } = useAssetLocations();
  const deleteMutation = useDeleteAsset();

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [condition, setCondition] = useState("");
  const [location, setLocation] = useState("");
  const [warrantyOnly, setWarrantyOnly] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<AssetListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("assets", ASSET_COLUMNS);

  useEffect(() => {
    setPage(1);
  }, [search, category, status, condition, location, warrantyOnly]);

  const params = useMemo(
    () => ({
      page,
      limit,
      search: search.trim() || undefined,
      category: category || undefined,
      status: status || undefined,
      condition: condition || undefined,
      location: location || undefined,
      warrantyExpiringSoon: warrantyOnly ? true : undefined,
    }),
    [page, limit, search, category, status, condition, location, warrantyOnly],
  );

  const { data, isLoading, isError } = useAssetList(params);
  const items = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  function handleDelete(asset: AssetListItem) {
    setDeleteTarget({ id: asset.id, name: asset.name });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.id);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Resources" },
        { label: "Assets" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Monitor
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Assets</h1>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              module="assets"
              filters={{
                search: search.trim() || undefined,
                category: category || undefined,
                status: status || undefined,
                condition: condition || undefined,
              }}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={ASSET_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Asset
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard label="Total" value={stats?.total ?? 0} />
          <StatCard label="Available" value={stats?.available ?? 0} tone="green" />
          <StatCard label="In Use" value={stats?.inUse ?? 0} tone="blue" />
          <StatCard label="Under Repair" value={stats?.underRepair ?? 0} tone="amber" />
          <StatCard
            label="Warranty Expiring"
            value={stats?.warrantyExpiringSoon ?? 0}
            tone="red"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-[280px]">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              placeholder="Search assets…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-44"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-40"
          >
            <option value="">All Status</option>
            <option value="available">Available</option>
            <option value="in_use">In Use</option>
            <option value="under_repair">Under Repair</option>
            <option value="retired">Retired</option>
            <option value="lost">Lost</option>
          </Select>
          <Select
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            className="w-40"
          >
            <option value="">All Conditions</option>
            {conditions.map((c) => (
              <option key={c.id} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <Select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="w-44"
          >
            <option value="">All Locations</option>
            {locations.map((l) => (
              <option key={l.id} value={l.value}>
                {l.label}
              </option>
            ))}
          </Select>
          <button
            type="button"
            onClick={() => setWarrantyOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 h-9 rounded-md border px-3 text-xs font-medium ${
              warrantyOnly
                ? "bg-[hsl(0,70%,96%)] text-[hsl(0,70%,40%)]"
                : "bg-white text-muted-foreground"
            }`}
            style={{ borderColor: BORDER }}
          >
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
            Warranty Expiring
          </button>
        </div>

        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: BORDER }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
              Loading assets…
            </div>
          ) : isError ? (
            <div className="py-16 text-center text-sm text-destructive">
              Failed to load assets.
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No assets match the current filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead
                className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground"
                style={{ background: "hsl(210,30%,97%)" }}
              >
                <tr>
                  {isVisible("code") && <th className="px-4 py-3 w-24">#</th>}
                  {isVisible("name") && <th className="px-4 py-3">Name</th>}
                  {isVisible("category") && (
                    <th className="px-4 py-3">Category</th>
                  )}
                  {isVisible("brand") && (
                    <th className="px-4 py-3">Brand / Model</th>
                  )}
                  {isVisible("condition") && (
                    <th className="px-4 py-3">Condition</th>
                  )}
                  {isVisible("status") && (
                    <th className="px-4 py-3">Status</th>
                  )}
                  {isVisible("assignedToName") && (
                    <th className="px-4 py-3">Assigned To</th>
                  )}
                  {isVisible("projectName") && (
                    <th className="px-4 py-3">Project</th>
                  )}
                  {isVisible("warranty") && (
                    <th className="px-4 py-3">Warranty</th>
                  )}
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((a) => {
                  const Icon = categoryIcon(a.category);
                  const w = warrantyCell(a.warrantyExpiry);
                  return (
                    <tr
                      key={a.id}
                      className="cursor-pointer hover:bg-[hsl(210,50%,98%)]"
                      onClick={() => navigate(`/assets/${a.id}`)}
                    >
                      {isVisible("code") && (
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                          {a.code}
                        </td>
                      )}
                      {isVisible("name") && (
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">
                            {a.name}
                          </div>
                          {(a.serialNumber || a.assetTag) && (
                            <div className="font-mono text-[11px] text-muted-foreground">
                              {a.serialNumber ? `SN: ${a.serialNumber}` : ""}
                              {a.serialNumber && a.assetTag ? " · " : ""}
                              {a.assetTag ? `Tag: ${a.assetTag}` : ""}
                            </div>
                          )}
                        </td>
                      )}
                      {isVisible("category") && (
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <Icon
                              className="h-3.5 w-3.5 shrink-0"
                              style={{ color: categoryColor(a.category) }}
                              strokeWidth={1.5}
                            />
                            <span className="capitalize">
                              {a.category.replace(/_/g, " ")}
                            </span>
                          </span>
                        </td>
                      )}
                      {isVisible("brand") && (
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {[a.brand, a.model].filter(Boolean).join(" ") || "—"}
                        </td>
                      )}
                      {isVisible("condition") && (
                        <td className="px-4 py-3">
                          <ConditionBadge value={a.condition} />
                        </td>
                      )}
                      {isVisible("status") && (
                        <td className="px-4 py-3">
                          <StatusBadge value={a.status} />
                        </td>
                      )}
                      {isVisible("assignedToName") && (
                        <td className="px-4 py-3 text-xs">
                          {a.assignedToName ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold"
                                style={{
                                  background: "hsl(210,55%,92%)",
                                  color: "hsl(210,100%,28%)",
                                }}
                              >
                                {initialsOf(a.assignedToName)}
                              </span>
                              {a.assignedToName}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      )}
                      {isVisible("projectName") && (
                        <td className="px-4 py-3 text-xs">
                          {a.projectName ? (
                            <span className="inline-flex items-center gap-1">
                              <FolderKanban
                                className="h-3 w-3 text-muted-foreground"
                                strokeWidth={1.5}
                              />
                              {a.projectName}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      )}
                      {isVisible("warranty") && (
                        <td className="px-4 py-3 text-xs">
                          <div className="flex items-center gap-1.5">
                            <span style={{ color: w.color }}>{w.text}</span>
                            {w.pill && (
                              <span
                                className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                                style={{
                                  background:
                                    w.pill === "Expired"
                                      ? "hsl(0,70%,94%)"
                                      : "hsl(38,90%,94%)",
                                  color:
                                    w.pill === "Expired"
                                      ? "hsl(0,70%,40%)"
                                      : "hsl(35,90%,32%)",
                                }}
                              >
                                {w.pill}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {a.status === "available" && (
                            <button
                              type="button"
                              onClick={() => setAssignTarget(a)}
                              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                              title="Assign"
                            >
                              <UserPlus
                                className="h-3.5 w-3.5"
                                strokeWidth={1.5}
                              />
                            </button>
                          )}
                          {a.status === "in_use" && (
                            <button
                              type="button"
                              onClick={() => navigate(`/assets/${a.id}`)}
                              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                              title="Return — open detail page"
                            >
                              <UserMinus
                                className="h-3.5 w-3.5"
                                strokeWidth={1.5}
                              />
                            </button>
                          )}
                          <RowActions
                            onView={() => navigate(`/assets/${a.id}`)}
                            onEdit={() => navigate(`/assets/${a.id}`)}
                            onDelete={() => handleDelete(a)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "9px 14px",
              borderTop: `1px solid ${BORDER}`,
              background: "hsl(210,30%,97%)",
              fontSize: "12px",
              color: "hsl(215,16%,47%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>
                Showing {startItem}-{endItem} of {total} entries
              </span>
              <span style={{ marginLeft: "8px" }}>Show</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                style={{
                  height: "26px",
                  padding: "0 4px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "4px",
                  fontSize: "12px",
                  background: "white",
                  cursor: "pointer",
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  width: "28px",
                  height: "28px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "4px",
                  background: "white",
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                  opacity: page <= 1 ? 0.4 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ‹
              </button>
              <span style={{ padding: "0 10px" }}>
                {totalPages === 0 ? "0" : page} / {totalPages || 1}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{
                  width: "28px",
                  height: "28px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "4px",
                  background: "white",
                  cursor: page >= totalPages ? "not-allowed" : "pointer",
                  opacity: page >= totalPages ? 0.4 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      <NewAssetDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      {assignTarget && (
        <AssignAssetModal
          isOpen={!!assignTarget}
          onClose={() => setAssignTarget(null)}
          assetId={assignTarget.id}
          assetName={assignTarget.name}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete asset?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Assets list.`
            : ""
        }
        onConfirm={confirmDelete}
      />

    </AppLayout>
  );
}
